import { SidebarMenu } from '../_models/menu/SidebarMenu.model';
import { CompanyMenuShortcut, findCompanyShortcut } from './company-menu-shortcuts';
import { findMenuHub, menuHubAccessibile, normMenuUrl } from './menu-hub';
import { asMenuOrgNode, conPadriInTendina, flattenMenuLeaves, MenuOrgChild, MenuOrgNode, MenuTreeAccess, newMenuOrgId, placeUnassignedLeaf, seedMenuOrgNodes } from './menu-organization';

// Albero dell'editor visuale del menu (modifica direttamente sul menu del portale).
// Tre livelli come i menu di Word: la barra (voci e menu), il menu che si apre sotto
// (voci e sottomenu) e il sottomenu che si apre di lato (solo voci). Menu e sottomenu
// sono gruppi: raccolgono voci, non sono pagine. Si legge e si scrive lo stesso
// cmp_menu_org dell'organizzazione menu: un gruppo e' una categoria (il sottomenu una
// sottocategoria), una voce e' una pagina o un collegamento. Le pagine non si tolgono
// (dipendono da servizi e ruoli dell'azienda), si spostano soltanto.

export interface MenuEdVoce {
	uid: string;
	tipo: 'pagina' | 'collegamento';
	pg_id?: number;
	key?: string;
	/** Nome della pagina in questo menu (vuoto = titolo della pagina) */
	label?: string;
	/** Icona in questo menu (vuota = icona della pagina o della card) */
	icon?: string;
}

export interface MenuEdMenu {
	uid: string;
	tipo: 'menu';
	/** id della categoria nel profilo: String(pg_id) per i gruppi nati da una pagina col sottomenu */
	catId: string;
	label: string;
	icon: string;
	/** Voci e, in un menu della barra, sottomenu */
	voci: MenuEdNodo[];
}

export type MenuEdNodo = MenuEdMenu | MenuEdVoce;

/** Livelli: 0 = barra, 1 = menu (si apre sotto), 2 = sottomenu (si apre di lato). Solo la barra e i menu contengono gruppi. */
export const LIVELLO_SOTTOMENU = 2;

export function nuovoUid(): string {
	return newMenuOrgId('u');
}

/** Il gruppo contiene a sua volta dei gruppi (allora non puo' diventare un sottomenu). */
export function haSottogruppi(n: MenuEdNodo): boolean {
	return n.tipo === 'menu' && n.voci.some((v) => v.tipo === 'menu');
}

const ACCESSO_EDITOR: MenuTreeAccess<MenuEdNodo> = {
	children: (n) => (n.tipo === 'menu' ? n.voci : undefined),
	pageId: (n) => (n.tipo === 'pagina' ? n.pg_id : undefined),
	isCategoryOf: (n, raw) => n.tipo === 'menu' && (n.catId === String(raw.pg_id) || n.label === raw.pg_title),
};

/**
 * Albero dell'editor dai nodi del profilo attivo (null = menu standard). Le pagine col
 * sottomenu diventano gruppi con la pagina come prima voce, come le mostra il portale;
 * le sottocategorie diventano sottomenu. Solo oltre il terzo livello si appiattisce
 * (`appiattito` lo segnala). Le pagine dell'azienda che il profilo non nomina finiscono
 * dove starebbero nel menu standard, come in applyMenuOrganization.
 */
export function costruisciAlberoEditor(raw: SidebarMenu[], nodes: MenuOrgNode[] | null, nascoste: number[] = []): { albero: MenuEdNodo[]; appiattito: boolean } {
	const pagine = new Map<number, SidebarMenu>(flattenMenuLeaves(raw || []).map((l) => [l.pg_id, l]));
	// le pagine tolte dal menu non entrano nell'albero (ne' dal profilo ne' come pagine da collocare)
	const usate = new Set<number>(nascoste);
	const usatiCollegamenti = new Set<string>();
	let appiattito = false;

	const voce = (n: MenuOrgNode): MenuEdVoce | null => {
		if (n.type === 'item') {
			if (!pagine.has(n.pg_id) || usate.has(n.pg_id)) return null;
			usate.add(n.pg_id);
			return { uid: nuovoUid(), tipo: 'pagina', pg_id: n.pg_id, ...(n.label ? { label: n.label } : {}), ...(n.icon ? { icon: n.icon } : {}) };
		}
		if (n.type === 'shortcut') {
			if (!findCompanyShortcut(n.key) || usatiCollegamenti.has(n.key)) return null;
			usatiCollegamenti.add(n.key);
			return { uid: nuovoUid(), tipo: 'collegamento', key: n.key, ...(n.icon ? { icon: n.icon } : {}) };
		}
		return null;
	};

	// oltre il terzo livello: le voci del ramo, in ordine, nel sottomenu che lo contiene
	const appiattisci = (n: MenuOrgNode, out: MenuEdNodo[]) => {
		if (n.type !== 'category') {
			const v = voce(n);
			if (v) out.push(v);
		}
		for (const c of (n.type === 'category' ? n.cat.items : n.items) || []) {
			const figlio = asMenuOrgNode(c);
			if (figlio) appiattisci(figlio, out);
		}
	};

	const gruppo = (n: MenuOrgNode, livello: number): MenuEdMenu | null => {
		const lista: MenuEdNodo[] = [];
		if (n.type === 'category') {
			contenuto(n.cat.items, livello, lista);
			return { uid: nuovoUid(), tipo: 'menu', catId: n.cat.id, label: n.cat.label, icon: n.cat.icon || 'pi pi-folder', voci: lista };
		}
		// pagina (o collegamento) col sottomenu: un gruppo che la ha come prima voce
		const padre = voce(n);
		if (padre) lista.push(padre);
		contenuto(n.items || [], livello, lista);
		if (!lista.length) return null;
		if (n.type === 'item') {
			const pagina = pagine.get(n.pg_id);
			return { uid: nuovoUid(), tipo: 'menu', catId: String(n.pg_id), label: n.label || pagina?.pg_title || '', icon: pagina?.styleclass || 'pi pi-folder', voci: lista };
		}
		const sc = findCompanyShortcut(n.key);
		return { uid: nuovoUid(), tipo: 'menu', catId: 'sc_' + n.key, label: sc?.labelKey || n.key, icon: sc?.icon || 'pi pi-folder', voci: lista };
	};

	// figli di un contenitore al livello dato (0 barra, 1 menu, 2 sottomenu)
	function contenuto(children: MenuOrgChild[], livello: number, out: MenuEdNodo[]) {
		for (const child of children || []) {
			const n = asMenuOrgNode(child);
			if (!n) continue;
			if (n.type !== 'category' && !n.items?.length) {
				const v = voce(n);
				if (v) out.push(v);
			} else if (livello < LIVELLO_SOTTOMENU) {
				const g = gruppo(n, livello + 1);
				if (g) out.push(g);
			} else {
				appiattito = true;
				appiattisci(n, out);
			}
		}
	}

	const albero: MenuEdNodo[] = [];
	contenuto(nodes ?? seedMenuOrgNodes(raw || []), 0, albero);

	// pagine nuove o mai collocate: dove le mette il menu standard (padri gia' in tendina)
	const standard = conPadriInTendina(raw || []);
	for (const leaf of flattenMenuLeaves(raw || [])) {
		if (!usate.has(leaf.pg_id)) {
			usate.add(leaf.pg_id);
			placeUnassignedLeaf(albero, leaf, standard, { uid: nuovoUid(), tipo: 'pagina', pg_id: leaf.pg_id }, ACCESSO_EDITOR);
		}
	}
	return { albero, appiattito };
}

/** Nodi del profilo (cmp_menu_org) dall'albero dell'editor: i gruppi sono categorie, i sottomenu sottocategorie. */
export function alberoEditorANodi(albero: MenuEdNodo[]): MenuOrgNode[] {
	const nodo = (n: MenuEdNodo): MenuOrgNode => {
		if (n.tipo === 'menu') return { type: 'category', cat: { id: n.catId, label: n.label, icon: n.icon, items: n.voci.map(nodo) } };
		if (n.tipo === 'pagina') return { type: 'item', pg_id: n.pg_id!, ...(n.label ? { label: n.label } : {}), ...(n.icon ? { icon: n.icon } : {}) };
		return { type: 'shortcut', key: n.key!, ...(n.icon ? { icon: n.icon } : {}) };
	};
	return albero.map(nodo);
}

/** Funzione modulo di un ruolo (endpoint menu/manager/role-preview). */
export interface FunzioneRuolo {
	rl_id: number;
	md_nome: string;
	mdfu_nome: string;
	mdfu_function: string;
}

/**
 * Ruoli che vedono ogni pagina, come prt_sidebar_menu_get: i ruoli della pagina e,
 * per una pagina col sottomenu, anche quelli delle sue sotto-pagine (il padre compare
 * se l'utente ne vede almeno una). role_ids arriva completo solo agli ADMIN di portale.
 */
export function ruoliDellePagine(raw: SidebarMenu[]): Map<number, Set<number>> {
	const out = new Map<number, Set<number>>();
	const visita = (items: SidebarMenu[]): Set<number> => {
		const tutti = new Set<number>();
		for (const item of items || []) {
			const ruoli = new Set<number>(item.roleIds || []);
			visita(item.submenu || []).forEach((r) => ruoli.add(r));
			if (typeof item.pg_id === 'number') out.set(item.pg_id, ruoli);
			ruoli.forEach((r) => tutti.add(r));
		}
		return tutti;
	};
	visita(raw || []);
	return out;
}

/**
 * Il collegamento compare a chi ha la funzione del modulo, come la card della pagina (buildShortcutLeaves);
 * un cruscotto a chi apre una pagina della sezione (`puoAprire`: url normalizzato -> il ruolo vede quella pagina).
 */
export function collegamentoVisibile(sc: CompanyMenuShortcut | undefined, funzioni: FunzioneRuolo[], rfi: boolean, puoAprire: (url: string) => boolean = () => false): boolean {
	if (!sc || (sc.soloRfi && !rfi)) return false;
	if (sc.hub) {
		const hub = findMenuHub(sc.hub);
		return !!hub && menuHubAccessibile(hub, puoAprire);
	}
	if (sc.seVedeUrl) return puoAprire(normMenuUrl(sc.seVedeUrl));
	if (sc.qualsiasiFunzione) return !!sc.modulo && funzioni.some((f) => f.md_nome === sc.modulo);
	if (sc.sempre) return true;
	return !!sc.modulo && !!sc.funzione && funzioni.some((f) => f.md_nome === sc.modulo && (f.mdfu_nome === sc.funzione || f.mdfu_function === sc.funzione));
}

/**
 * url (normalizzati) delle pagine che le sezioni a schede attive tolgono dal menu,
 * con l'etichetta della sezione: nell'editor quelle voci si segnalano, perche' nel
 * portale stanno nelle schede della sezione e non nel menu (collapseMenuHubs).
 */
export function urlNelleSchede(hubKeys: string[] | null | undefined, cmpId: number): Map<string, string> {
	const out = new Map<string, string>();
	for (const key of hubKeys || []) {
		const hub = findMenuHub(key);
		// la pagina padre della sezione resta nel menu: diventa la voce che apre il cruscotto
		const padre = normMenuUrl(hub?.parentUrl);
		for (const tab of hub?.tabs || []) {
			for (const url of tab.urls) {
				const norm = normMenuUrl(url.replace(/:cmp/g, String(cmpId)));
				if (norm !== padre) out.set(norm, hub!.label);
			}
		}
	}
	return out;
}
